<template>
    <div class="soe-root">
        <!-- ══ PANEL PRINCIPAL: Ítems de la Orden ════════════════════════════ -->
        <div class="soe-hdr">
            <div class="soe-hdr-left">
                <div class="soe-hdr-icon">
                    <v-icon color="white" size="20">mdi-briefcase-check-outline</v-icon>
                </div>
                <div>
                    <div class="soe-hdr-title">Entradas de Orden de Servicio</div>
                    <div class="soe-hdr-sub" v-if="order">
                        {{ order.OrdDocumentNumber }} &nbsp;·&nbsp; {{ order.SupName }}
                    </div>
                </div>
            </div>
            <v-chip small dark color="primary" v-if="order">OrdID: {{ order.OrdID }}</v-chip>
        </div>

        <!-- Tabla de ítems (OddID) -->
        <div v-if="loadingItems" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" />
            <p class="mt-2 grey--text">Cargando ítems de la orden...</p>
        </div>
        <div v-else-if="!orderItems.length" class="soe-empty">
            <v-icon color="grey lighten-2" size="48">mdi-inbox-outline</v-icon>
            <p class="grey--text mt-2">No hay ítems en esta orden de servicio</p>
        </div>
        <v-data-table v-else :headers="headers" :items="orderItems" dense item-key="OddID" class="soe-table"
            hide-default-footer :items-per-page="-1" @click:row="selectItem">

            <!-- Descripción -->
            <template v-slot:item.OddDescription="{ item }">
                <div class="soe-desc-cell">
                    <div class="font-weight-medium" style="font-size:12px">{{ item.OddDescription }}</div>
                    <div style="font-size:10px;color:#999">Cta: {{ item.AcoName }} · {{ item.BslName }}</div>
                </div>
            </template>

            <!-- Monto -->
            <template v-slot:item.OddAmount="{ item }">
                <span class="primary--text font-weight-bold">{{ item.OddAmount | numberDefault }}</span>
            </template>

            <!-- Progreso -->
            <template v-slot:item._progress="{ item }">
                <div class="soe-progress-cell">
                    <v-progress-linear :value="item._progress || 0" height="8" rounded
                        :color="(item._progress || 0) >= 100 ? '#00c853' : (item._progress || 0) >= 50 ? '#ff9800' : '#1976d2'" />
                    <v-chip x-small dark class="ml-2"
                        :color="(item._progress || 0) >= 100 ? 'success' : (item._progress || 0) >= 50 ? 'warning' : 'primary'">
                        {{ (item._progress || 0).toFixed(0) }}%
                    </v-chip>
                </div>
            </template>

            <!-- Actividades count -->
            <template v-slot:item._actCount="{ item }">
                <v-chip x-small :color="item._actCount ? 'primary' : 'grey lighten-2'" :dark="!!item._actCount">
                    {{ item._actCount || 0 }}
                </v-chip>
            </template>

            <!-- Acción -->
            <template v-slot:item._action="{ item }">
                <v-btn small depressed color="primary" class="rounded-lg" @click.stop="openItemActivities(item)">
                    <v-icon small left>mdi-format-list-checks</v-icon>
                    Gestionar
                </v-btn>
            </template>
        </v-data-table>


        <!-- ══ DIALOG: Actividades del ítem ══════════════════════════════════ -->
        <v-dialog v-model="dlgAct" persistent width="1200" v-if="dlgAct">
            <v-card class="act-card rounded-xl overflow-hidden">

                <!-- ── Header ─────────────────────────────────────── -->
                <div class="act-hdr">
                    <div class="d-flex align-center" style="min-width:0;flex:1">
                        <div class="act-hdr-icon">
                            <v-icon color="white" size="18">mdi-format-list-checks</v-icon>
                        </div>
                        <div class="act-hdr-texts ml-3">
                            <div class="act-hdr-title">Actividades Derivadas</div>
                            <div class="act-hdr-sub" v-if="activeItem">
                                {{ activeItem.OddDescription }}
                            </div>
                        </div>
                    </div>
                    <div class="d-flex align-center" style="gap:8px;flex-shrink:0">
                        <v-chip small dark
                            :color="itemProgress >= 100 ? '#00c853' : itemProgress >= 50 ? '#ff9800' : 'rgba(255,255,255,0.2)'"
                            class="act-badge">
                            <v-icon x-small left>mdi-chart-donut</v-icon>
                            {{ itemProgress.toFixed(0) }}% avance
                        </v-chip>
                        <v-btn icon dark small @click="dlgAct = false">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </div>
                </div>

                <!-- ── KPI Bar ─────────────────────────────────────── -->
                <div class="act-kpi-bar" v-if="activeItem">
                    <div class="act-kpi-cell">
                        <div class="act-kpi-label">Monto del ítem</div>
                        <div class="act-kpi-value" style="color:#1976d2">
                            {{ activeItem.OddAmount | numberDefault }}
                        </div>
                    </div>
                    <div class="act-kpi-sep"></div>
                    <div class="act-kpi-cell">
                        <div class="act-kpi-label">Costo asignado</div>
                        <div class="act-kpi-value" :class="costOver ? 'act-over' : 'act-ok'">
                            {{ totalCost | numberDefault }}
                        </div>
                    </div>
                    <div class="act-kpi-sep"></div>
                    <div class="act-kpi-cell">
                        <div class="act-kpi-label">Diferencia</div>
                        <div class="act-kpi-value" :class="(activeItem.OddAmount - totalCost) < 0 ? 'act-over' : ''"
                            style="color:#555">
                            {{ (activeItem.OddAmount - totalCost) | numberDefault }}
                        </div>
                    </div>
                    <div class="act-kpi-sep"></div>
                    <div class="act-kpi-cell" style="flex:2;min-width:200px">
                        <div class="act-kpi-label mb-1">
                            Progreso global —
                            {{activities.filter(a => a.SoaCompleted).length}}/{{ activities.length }} completadas
                            <span v-if="activities.length && weightTotal !== 100" class="act-kpi-warn ml-2">
                                Pesos: {{ weightTotal }}%/100%
                            </span>
                        </div>
                        <v-progress-linear :value="itemProgress" height="10" rounded
                            :color="itemProgress >= 100 ? '#00c853' : itemProgress >= 50 ? '#ff9800' : '#1976d2'">
                            <template v-slot:default>
                                <span style="font-size:9px;color:#fff;font-weight:700">{{ itemProgress.toFixed(0)
                                    }}%</span>
                            </template>
                        </v-progress-linear>
                    </div>
                </div>

                <!-- ── Body: dos columnas ──────────────────────────── -->
                <div class="act-body">

                    <!-- LEFT: Formulario (30%) -->
                    <div class="act-panel-form">
                        <div class="act-form-title">
                            <v-icon small :color="editingAct ? '#ff9800' : '#1976d2'" class="mr-1">
                                {{ editingAct ? 'mdi-pencil-circle' : 'mdi-plus-circle' }}
                            </v-icon>
                            {{ editingAct ? 'Editar actividad' : 'Nueva actividad' }}
                        </div>

                        <!-- Descripción -->
                        <div class="act-field-group">
                            <label class="act-label">Descripción <span class="act-required">*</span></label>
                            <v-text-field v-model="form.SoaDescription" placeholder="Describe la actividad..." outlined
                                dense hide-details class="act-input" background-color="white" />
                        </div>

                        <!-- Costo -->
                        <div class="act-field-group">
                            <label class="act-label">Costo correspondiente (S/)</label>
                            <v-text-field v-model.number="form.SoaCost" outlined dense hide-details type="number"
                                min="0" prefix="S/" class="act-input" background-color="white" @input="onCostInput" />
                            <label class="act-fill-check mt-2">
                                <input type="checkbox" v-model="fillMaxCost" @change="onFillMaxCostChange"
                                    class="act-fill-check-input" />
                                <span class="act-fill-check-box">
                                    <v-icon x-small :color="fillMaxCost ? 'success' : 'grey'">
                                        {{ fillMaxCost ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                                    </v-icon>
                                </span>
                                <span class="act-fill-check-text">Seleccionar monto disponible</span>
                            </label>
                            <div v-if="fillMaxCost && activeItem" class="act-fill-hint mt-1">
                                <v-icon x-small color="teal" class="mr-1">mdi-information-outline</v-icon>
                                Disponible: S/ {{(parseFloat(activeItem.OddAmount || 0) -
                                    activities.filter(a => !editingAct || a.SoaID !== editingAct.SoaID).reduce((s, a) => s +
                                        (parseFloat(a.SoaCost) || 0), 0)).toFixed(2)
                                }}
                            </div>
                        </div>

                        <!-- Peso % -->
                        <div class="act-field-group">
                            <label class="act-label">Consumo Presupuesto (%) <span class="act-required">*</span></label>
                            <div class="act-field-hint">
                                Disponible: <strong>{{ (100 - weightTotal + (editingAct ?
                                    parseFloat(editingAct.SoaWeightPercent) || 0 : 0)).toFixed(1) }}%</strong>
                            </div>
                            <v-text-field v-model.number="form.SoaWeightPercent" outlined dense hide-details
                                type="number" min="0" max="100" suffix="%" class="act-input" background-color="white"
                                @input="onWeightInput" />
                            <v-progress-linear :value="form.SoaWeightPercent" height="4" rounded class="mt-1"
                                :color="form.SoaWeightPercent > 100 ? 'error' : form.SoaWeightPercent >= 70 ? 'warning' : 'primary'" />
                        </div>

                        <!-- Observación -->
                        <div class="act-field-group">
                            <label class="act-label">Observación</label>
                            <v-textarea v-model="form.SoaObservation" placeholder="Notas adicionales..." outlined dense
                                hide-details rows="3" class="act-input" background-color="white" no-resize />
                        </div>

                        <!-- Botones -->
                        <div class="act-form-actions">
                            <v-btn depressed :color="editingAct ? 'warning' : 'primary'" @click="saveActivity"
                                :disabled="!canSaveActivity" :loading="savingAct" class="act-btn-save rounded-lg" block>
                                <v-icon small left>{{ editingAct ? 'mdi-content-save' : 'mdi-check-circle-outline'
                                    }}</v-icon>
                                {{ editingAct ? 'Actualizar' : 'Registrar actividad' }}
                            </v-btn>
                            <v-btn v-if="editingAct" text color="grey darken-1" @click="cancelEdit"
                                class="act-btn-cancel mt-2 rounded-lg" block small>
                                <v-icon small left>mdi-close</v-icon> Cancelar edición
                            </v-btn>
                        </div>

                        <!-- Alertas de validación -->
                        <v-alert v-if="!editingAct && weightTotal >= 100" type="success" dense text
                            class="mt-3 rounded-lg" style="font-size:11px" icon="mdi-check-decagram">
                            El 100% de la orden ya está asignado.
                        </v-alert>
                        <v-alert v-else-if="formWeightExceeded" type="error" dense text class="mt-3 rounded-lg"
                            style="font-size:11px" icon="mdi-alert-circle">
                            El peso supera el 100% disponible.
                        </v-alert>

                        <v-alert v-if="formCostExceeded" type="error" dense text class="mt-2 rounded-lg"
                            style="font-size:11px" icon="mdi-alert-circle">
                            El costo supera el monto del ítem.
                        </v-alert>
                    </div>

                    <!-- RIGHT: Lista de actividades (70%) -->
                    <div class="act-panel-list">
                        <!-- Gráficas 3D -->
                        <div class="act-charts-row mb-3" v-if="activities.length > 0 && !loadingActs">
                            <v-row dense>
                                <!-- <v-col cols="6">
                                    <v-card outlined class="rounded-lg pa-2 shadow-sm"
                                        style="background: white; border-color: #e4e8f0;">
                                        <div
                                            class="text-caption font-weight-bold grey--text text-center text-uppercase">
                                            Distribución de Avance (%)</div>
                                        <highcharts :options="chartProgressOptions" />
                                    </v-card>
                                </v-col> -->
                                <v-col cols="12" lg="6">
                                    <v-card outlined class="rounded-lg pa-2 shadow-sm"
                                        style="background: white; border-color: #e4e8f0;">
                                        <div
                                            class="text-caption font-weight-bold grey--text text-center text-uppercase">
                                            Distribución de Costo (S/)</div>
                                        <highcharts :options="chartCostOptions" />
                                    </v-card>
                                </v-col>
                            </v-row>
                        </div>

                        <div class="act-list-title">
                            <v-icon small color="#1976d2" class="mr-1">mdi-view-list-outline</v-icon>
                            Actividades registradas
                            <v-chip small color="primary" dark class="ml-2 px-2">
                                {{ activities.length }}
                            </v-chip>
                        </div>

                        <!-- Loading -->
                        <div v-if="loadingActs" class="d-flex justify-center align-center" style="height:200px">
                            <div class="text-center">
                                <v-progress-circular indeterminate color="primary" size="36" />
                                <div class="mt-2" style="font-size:12px;color:#999">Cargando actividades...</div>
                            </div>
                        </div>

                        <!-- Empty -->
                        <div v-else-if="!activities.length" class="act-list-empty">
                            <v-icon color="grey lighten-2" size="52">mdi-playlist-plus</v-icon>
                            <p class="mt-2">Sin actividades registradas</p>
                            <p style="font-size:11px">Usa el formulario para agregar la primera actividad</p>
                        </div>

                        <!-- Lista -->
                        <div v-else class="act-list-scroll">
                            <div v-for="(act, i) in activities" :key="act.SoaID" class="act-item"
                                :class="{ 'act-item-done': act.SoaCompleted, 'act-item-editing': editingAct && editingAct.SoaID === act.SoaID }">

                                <!-- Nº + Switch "Finalizar Actividad" -->
                                <div class="act-item-left">
                                    <div class="act-item-num">#{{ i + 1 }}</div>
                                    <div class="act-switch-wrap" :class="{ 'act-switch-active': act.SoaCompleted }">
                                        <div class="act-switch-label">
                                            <v-icon x-small :color="act.SoaCompleted ? '#00c853' : '#bbb'" class="mb-1">
                                                {{ act.SoaCompleted ? 'mdi-check-decagram' : 'mdi-flag-outline' }}
                                            </v-icon>
                                            <span>{{ act.SoaCompleted ? 'Finalizada' : 'Finalizar' }}</span>
                                            <!-- <span class="act-switch-sub">Actividad</span> -->
                                        </div>
                                        <v-switch v-model="act.SoaCompleted" hide-details dense inset :true-value="1"
                                            :false-value="0"
                                            :color="act.SoaCompleted == 1 ? 'success' : 'grey lighten-1'"
                                            class="act-switch mt-0 pt-0" @change="toggleComplete(act)" />
                                    </div>
                                </div>

                                <!-- Contenido -->
                                <div class="act-item-body">
                                    <div class="act-item-desc"
                                        :class="{ 'text-decoration-line-through grey--text text--lighten-1': act.SoaCompleted }">
                                        {{ act.SoaDescription }}
                                    </div>
                                    <div v-if="act.SoaObservation" class="act-item-obs mt-1">
                                        <v-icon x-small color="grey" class="mr-1">mdi-text</v-icon>
                                        {{ act.SoaObservation }}
                                    </div>

                                    <!-- Explicación sencilla de impacto -->
                                    <div class="act-item-impact mt-3">
                                        <div class="impact-pill" :class="act.SoaCompleted ? 'impact-pill-done' : ''">
                                            <v-icon x-small :color="act.SoaCompleted ? '#00c853' : '#888'"
                                                class="mr-1">mdi-chart-donut</v-icon>
                                            <span class="impact-text">
                                                Suma el <strong>{{ act.SoaWeightPercent }}%</strong> del progreso de la
                                                orden
                                            </span>
                                        </div>
                                        <div class="impact-pill">
                                            <v-icon x-small color="#00897b" class="mr-1">mdi-cash</v-icon>
                                            <span class="impact-text">
                                                Tiene un costo de <strong>S/ {{ act.SoaCost | numberDefault }}</strong>
                                            </span>
                                        </div>
                                        <div class="impact-pill"
                                            :class="act.SoaCompleted ? 'impact-pill-done' : 'impact-pill-pending'">
                                            <v-icon x-small :color="act.SoaCompleted ? '#00c853' : '#ff9800'"
                                                class="mr-1">
                                                {{ act.SoaCompleted ? 'mdi-check-circle' : 'mdi-clock-outline' }}
                                            </v-icon>
                                            <span class="impact-text font-weight-bold"
                                                :style="{ color: act.SoaCompleted ? '#00c853' : '#ff9800' }">
                                                {{ act.SoaCompleted ?
                                                    'ESTA ACTIVIDAD YA FUE SUMADA AL TOTAL'
                                                    : 'AÚN NO SUMA, ESPERANDO FINALIZAR' }}
                                            </span>
                                        </div>
                                    </div>

                                    <!-- Botón de Adjuntar Evidencias Multimedia (Separado) -->
                                    <div class="mt-3 pt-2" style="border-top: 1px dashed #e4e8f0;">
                                        <v-btn small
                                            :color="act._files && act._files.length ? 'primary' : 'grey lighten-2'"
                                            :outlined="!act._files || !act._files.length"
                                            :dark="!!(act._files && act._files.length)" class="rounded-lg"
                                            style="border-width: 1px; letter-spacing: .3px; text-transform: none; padding: 0 16px; height: 30px;"
                                            @click="openEvidence(act)" elevation="0">
                                            <v-icon small left
                                                :color="act._files && act._files.length ? 'white' : 'grey darken-1'">
                                                {{ act._files && act._files.length ? 'mdi-folder-multiple-image' :
                                                    'mdi-camera-plus' }}
                                            </v-icon>
                                            <span
                                                :class="act._files && act._files.length ? 'white--text' : 'grey--text text--darken-3'"
                                                style="font-size:11px; font-weight: 700;">
                                                {{ act._files && act._files.length ? `Evidencias Adjuntas
                                                (${act._files.length})` : 'Adjuntar Fotos o Documentos' }}
                                            </span>
                                        </v-btn>
                                    </div>
                                </div>

                                <!-- Acciones -->
                                <div class="act-item-actions">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on }">
                                            <v-btn icon small v-on="on" color="primary" @click="editAct(act)">
                                                <v-icon x-small>mdi-pencil-outline</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Editar</span>
                                    </v-tooltip>
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on }">
                                            <v-btn icon small v-on="on" color="error" @click="deleteAct(act)">
                                                <v-icon x-small>mdi-delete-outline</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Eliminar</span>
                                    </v-tooltip>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </v-card>
        </v-dialog>

        <!-- ══ DIALOG: Evidencias (Drag & Drop) ══════════════════════════════ -->
        <v-dialog v-model="dlgEv" width="800" v-if="dlgEv" scrollable>
            <v-card class="rounded-xl overflow-hidden act-ev-card" style="background:#f8f9fa;">
                <div class="act-ev-hdr">
                    <div class="d-flex align-center">
                        <div class="act-ev-hdr-icon">
                            <v-icon color="white" size="22">mdi-paperclip</v-icon>
                        </div>
                        <div class="ml-3" style="min-width:0">
                            <div class="act-ev-title">Evidencias Adjuntas</div>
                            <div class="act-ev-sub" v-if="activeAct">{{ activeAct.SoaDescription }}</div>
                        </div>
                    </div>
                    <v-btn icon dark small @click="dlgEv = false"
                        class="act-ev-close"><v-icon>mdi-close</v-icon></v-btn>
                </div>

                <v-card-text class="pa-5" style="max-height: calc(100vh - 200px); overflow-y: auto;">
                    <!-- Zona Drag & Drop -->
                    <div class="soe-drop-zone" :class="{ 'soe-drop-over': isDragOver }"
                        @dragover.prevent="isDragOver = true" @dragleave.prevent="isDragOver = false"
                        @drop.prevent="onDrop($event)" @click="$refs.fileInput.click()">
                        <div class="drop-zone-icon">
                            <v-icon size="56" :color="isDragOver ? 'primary' : '#a0aec0'">
                                {{ isDragOver ? 'mdi-cloud-upload' : 'mdi-tray-arrow-up' }}
                            </v-icon>
                        </div>
                        <h3 class="drop-zone-title mt-4 mb-1">
                            <strong>Haz clic</strong> o arrastra tus archivos aquí
                        </h3>
                        <p class="drop-zone-subtitle">PDF, Imágenes, Word o Excel (Máx. 10 MB)</p>
                        <v-btn depressed color="primary" class="mt-4 rounded-lg drop-zone-btn">
                            <v-icon small left>mdi-folder-open-outline</v-icon> Examinar archivos
                        </v-btn>
                        <input ref="fileInput" type="file" multiple hidden @change="onFileInput($event)" />
                    </div>

                    <!-- Archivos en cola -->
                    <v-slide-y-transition group>
                        <div v-if="pendingFiles.length" class="mt-5" key="queue">
                            <div class="soe-ev-section-title mb-3">
                                <v-icon small color="orange" class="mr-1">mdi-upload-network</v-icon> En cola para subir
                                ({{
                                    pendingFiles.length }})
                            </div>
                            <div class="ev-files-grid">
                                <div v-for="(f, i) in pendingFiles" :key="'p' + i" class="ev-file-card">
                                    <div class="ev-file-icon-wrap">
                                        <img v-if="f.preview" :src="f.preview" />
                                        <v-icon v-else size="24" :color="fileIconColor(f.name)">{{ fileIcon(f.name)
                                        }}</v-icon>
                                    </div>
                                    <div class="ev-file-info" :title="f.name">
                                        <div class="ev-file-name">{{ f.name }}</div>
                                        <div class="ev-file-meta">
                                            <span
                                                style="text-transform: uppercase; font-weight: 700; color: #94a3b8;">{{
                                                    (f.name || '').split('.').pop() }}</span>
                                            <span>&bull;</span>
                                            <span>{{ formatSize(f.size) }}</span>
                                        </div>
                                    </div>
                                    <div class="ev-file-actions">
                                        <v-btn icon class="ev-action-btn-sm" color="error"
                                            @click="pendingFiles.splice(i, 1)" title="Remover">
                                            <v-icon small>mdi-close</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                            <v-btn depressed color="orange darken-2" dark block class="mt-3 rounded-lg font-weight-bold"
                                :loading="uploadingFiles" @click="uploadPending" :disabled="!pendingFiles.length"
                                style="letter-spacing: .5px;">
                                <v-icon small left>mdi-cloud-upload</v-icon>
                                Confirmar subida de
                                {{ pendingFiles.length }}
                                archivo(s)
                            </v-btn>
                        </div>
                    </v-slide-y-transition>

                    <!-- Archivos subidos -->
                    <div class="mt-6" v-if="activeAct && activeAct._files && activeAct._files.length">
                        <v-divider class="mb-4"></v-divider>
                        <div class="soe-ev-section-title mb-3">
                            <v-icon small color="success" class="mr-1">mdi-check-circle</v-icon>
                            Archivos registrados en la actividad
                        </div>
                        <div class="ev-files-grid">
                            <div v-for="(ev, i) in activeAct._files" :key="'ev' + i"
                                class="ev-file-card ev-file-card-done">
                                <div class="ev-file-icon-wrap">
                                    <img v-if="isImageFile(ev.FileName) && ev._previewUrl" :src="ev._previewUrl" />
                                    <v-icon v-else size="24" :color="fileIconColor(ev.FileName)">
                                        {{ fileIcon(ev.FileName) }}
                                    </v-icon>
                                </div>
                                <div class="ev-file-info" :title="ev.FileName">
                                    <div class="ev-file-name">{{ ev.FileName }}</div>
                                    <div class="ev-file-meta">
                                        <span style="text-transform: uppercase; font-weight: 700; color: #94a3b8;">
                                            {{ ((ev.FileName || '').split('.').pop() || 'FILE').substring(0, 4) }}
                                        </span>
                                        <!-- Ocultar size temporalmente si no viene de API -->
                                        <span v-if="ev.EvSize">&bull;</span>
                                        <span v-if="ev.EvSize">{{ formatSize(ev.EvSize) }}</span>
                                    </div>
                                </div>
                                <div class="ev-file-actions">
                                    <v-btn icon class="ev-action-btn-sm" color="primary"
                                        @click.prevent="downloadEvidence(ev.FileName)" title="Descargar">
                                        <v-icon small>mdi-download</v-icon>
                                    </v-btn>
                                    <v-btn icon class="ev-action-btn-sm" color="error" @click="deleteEv(ev, i)"
                                        title="Eliminar">
                                        <v-icon small>mdi-delete-outline</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="loadingEv" class="text-center py-6 mt-4">
                        <v-progress-circular indeterminate size="32" color="primary" />
                        <div class="text-caption mt-2 grey--text">Cargando archivos...</div>
                    </div>
                    <div v-else class="text-center py-6 grey--text mt-4"
                        style="border: 1px dashed #cbd5e1; border-radius: 12px; background: #fff;">
                        <v-icon color="grey lighten-1" size="32" class="mb-2">mdi-folder-open-outline</v-icon>
                        <div style="font-size:13px; font-weight: 500;">Esta actividad aún no tiene archivos adjuntos.
                        </div>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import _OrderActivityService from "@/services/Logistics/ServiceOrderActivityService";
import _sOrder from "@/services/Logistics/OrderPurchaseService";
import _GenUploadFiles from "@/services/General/GenUploadFiles";

import Highcharts from 'highcharts';
import Highcharts3d from 'highcharts/highcharts-3d';
import { Chart } from 'highcharts-vue';

if (typeof Highcharts === 'object') {
    Highcharts3d(Highcharts);
}

export default {
    name: "ServiceOrderEntry",
    components: {
        highcharts: Chart
    },
    props: {
        order: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            orderItems: [],
            loadingItems: false,
            headers: [
                { text: "#", value: "Line", sortable: false, width: 50 },
                { text: "DESCRIPCIÓN / CUENTA", value: "OddDescription", sortable: false },
                { text: "CANT.", value: "OddQuantity", sortable: false, width: 70, align: "center" },
                { text: "MONTO", value: "OddAmount", sortable: false, width: 120, align: "right" },
                { text: "ACTIVIDADES", value: "_actCount", sortable: false, width: 100, align: "center" },
                { text: "PROGRESO", value: "_progress", sortable: false, width: 200 },
                { text: "", value: "_action", sortable: false, width: 130, align: "center" },
            ],
            dlgAct: false,
            activeItem: null,
            activities: [],
            loadingActs: false,
            savingAct: false,
            editingAct: null,
            form: {
                SoaID: 0,
                OddID: 0,
                OrdID: 0,
                SoaDescription: "",
                SoaWeightPercent: 0,
                SoaCost: 0,
                SoaCompleted: 0,
                SoaObservation: "",
                SoaStatus: 1,
                SecStatus: 1,
                UsrCreateID: 0
            },
            dlgEv: false,
            activeAct: null,
            isDragOver: false,
            pendingFiles: [],
            uploadingFiles: false,
            loadingEv: false,
            fillMaxCost: false,
        };
    },
    computed: {
        chartProgressOptions() {
            const data = this.activities.map(a => ({
                name: (a.SoaDescription || '').substring(0, 30) + ((a.SoaDescription || '').length > 30 ? '...' : ''),
                y: parseFloat(a.SoaWeightPercent) || 0
            }));
            const remaining = Math.max(0, 100 - this.weightTotal);
            if (remaining > 0) {
                data.push({ name: 'Faltante', y: remaining, color: '#e2e8f0' });
            }

            return {
                colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#6366f1', '#14b8a6'],
                chart: { type: 'pie', options3d: { enabled: true, alpha: 45, beta: 0 }, backgroundColor: 'transparent', height: 160, margin: [0, 0, 0, 0] },
                title: { text: null },
                tooltip: { pointFormat: '<b>{point.y}%</b>' },
                plotOptions: { pie: { innerSize: '45%', depth: 35, dataLabels: { enabled: true, format: '{point.name}', style: { fontSize: '9px', fontWeight: 'normal' }, distance: 10 }, showInLegend: false } },
                series: [{ name: 'Avance', colorByPoint: true, data: data }],
                credits: { enabled: false }
            };
        },
        chartCostOptions() {
            const data = this.activities.map(a => ({
                name: (a.SoaDescription || '').substring(0, 30) + ((a.SoaDescription || '').length > 30 ? '...' : ''),
                y: parseFloat(a.SoaCost) || 0
            }));
            const remaining = Math.max(0, parseFloat(this.activeItem?.OddAmount || 0) - this.totalCost);
            if (remaining > 0) {
                data.push({ name: 'Saldo disp.', y: remaining, color: '#e2e8f0' });
            }

            return {
                colors: ['#0ea5e9', '#22c55e', '#eab308', '#f43f5e', '#a855f7', '#d946ef', '#06b6d4', '#84cc16', '#8b5cf6', '#10b981'],
                chart: { type: 'pie', options3d: { enabled: true, alpha: 45, beta: 0 }, backgroundColor: 'transparent', height: 160, margin: [0, 0, 0, 0] },
                title: { text: null },
                tooltip: { pointFormat: '<b>S/ {point.y:,.2f}</b>' },
                plotOptions: { pie: { innerSize: '45%', depth: 35, dataLabels: { enabled: true, format: '{point.name}', style: { fontSize: '9px', fontWeight: 'normal' }, distance: 10 }, showInLegend: false } },
                series: [{ name: 'Costo', colorByPoint: true, data: data }],
                credits: { enabled: false }
            };
        },
        totalCost() {
            return this.activities.reduce((s, a) => s + (parseFloat(a.SoaCost) || 0), 0).toFixed(2);
        },
        costOver() {
            return this.activeItem && this.totalCost > parseFloat(this.activeItem.OddAmount || 0).toFixed(2);
        },
        formWeightExceeded() {
            const otherW = this.activities.filter(a => !this.editingAct || a.SoaID !== this.editingAct.SoaID).reduce((s, a) => s + (parseFloat(a.SoaWeightPercent) || 0), 0);
            return (otherW + parseFloat(this.form.SoaWeightPercent || 0)) > 100;
        },
        formCostExceeded() {
            const otherC = this.activities.filter(a => !this.editingAct || a.SoaID !== this.editingAct.SoaID).reduce((s, a) => s + (parseFloat(a.SoaCost) || 0), 0);
            return this.activeItem && ((otherC + parseFloat(this.form.SoaCost || 0)) > parseFloat(this.activeItem.OddAmount || 0));
        },
        canSaveActivity() {
            if (!this.form.SoaDescription || !this.form.SoaDescription.trim()) return false;
            if (parseFloat(this.form.SoaWeightPercent || 0) <= 0) return false;
            if (this.formWeightExceeded) return false;
            if (this.formCostExceeded) return false;
            if (!this.editingAct && this.weightTotal >= 100) return false;
            return true;
        },
        weightTotal() {
            return this.activities.reduce((s, a) => s + (parseFloat(a.SoaWeightPercent) || 0), 0);
        },
        itemProgress() {
            return Math.min(this.activities.filter(a => a.SoaCompleted == 1).reduce((s, a) => s + (parseFloat(a.SoaWeightPercent) || 0), 0), 100);
        },
    },
    watch: {
        order: {
            immediate: true, handler(v) {
                if (v && v.OrdID) this.fetchItems();
            }
        },
    },
    methods: {
        onWeightInput() {
            if (this._syncing) return;
            this._syncing = true;
            const pct = parseFloat(this.form.SoaWeightPercent) || 0;
            const itemAmount = parseFloat(this.activeItem?.OddAmount || 0);
            this.form.SoaCost = itemAmount > 0 ? parseFloat(((pct / 100) * itemAmount).toFixed(2)) : 0;
            this.fillMaxCost = false;
            this.$nextTick(() => { this._syncing = false; });
        },
        onCostInput() {
            if (this._syncing) return;
            this._syncing = true;
            const cost = parseFloat(this.form.SoaCost) || 0;
            const itemAmount = parseFloat(this.activeItem?.OddAmount || 0);
            this.form.SoaWeightPercent = itemAmount > 0 ? parseFloat(((cost / itemAmount) * 100).toFixed(2)) : 0;
            this.fillMaxCost = false;
            this.$nextTick(() => { this._syncing = false; });
        },
        onFillMaxCostChange() {
            if (this.fillMaxCost && this.activeItem) {
                const otherCost = this.activities
                    .filter(a => !this.editingAct || a.SoaID !== this.editingAct.SoaID)
                    .reduce((s, a) => s + (parseFloat(a.SoaCost) || 0), 0);
                const maxCost = Math.max(0, parseFloat(this.activeItem.OddAmount || 0) - otherCost);
                this.form.SoaCost = parseFloat(maxCost.toFixed(2));
                const itemAmount = parseFloat(this.activeItem.OddAmount || 0);
                this.form.SoaWeightPercent = itemAmount > 0 ? parseFloat(((this.form.SoaCost / itemAmount) * 100).toFixed(2)) : 0;
            }
        },
        fetchItems() {
            if (!this.order?.OrdID) return;
            this.loadingItems = true;
            _sOrder.byID({ OrdID: this.order.OrdID }, this.$fun.getUserID()).then(r => {
                const raw = r.data?.Detail || r.data || [];
                this.orderItems = raw.map(i => ({ ...i, _progress: 0, _actCount: 0 }));
                this.loadItemsProgress();
            }).catch(() => this.$fun.alert("Error al cargar ítems de la orden", "error"))
                .finally(() => { this.loadingItems = false; });
        },
        loadItemsProgress() {
            this.orderItems.forEach((item, idx) => {
                const params = {
                    filter: { OddID: item.OddID },
                    draw: 1,
                    start: 0,
                    length: 500
                };
                _OrderActivityService.pagination(params, this.$fun.getUserID()).then(r => {
                    const acts = r.data?.data || [];
                    const progress = Math.min(acts.filter(a => a.SoaCompleted == 1).reduce((s, a) => s + (parseFloat(a.SoaWeightPercent) || 0), 0), 100);
                    this.$set(this.orderItems, idx, { ...item, _actCount: acts.length, _progress: progress });
                }).catch(() => { });
            });
        },
        selectItem(item) {
            //  this.openItemActivities(item);
        },
        openItemActivities(item) {
            this.activeItem = item;
            this.dlgAct = true;
            this.cancelEdit();
            this.fetchActivities();
        },
        fetchActivities() {
            if (!this.activeItem) return;
            this.loadingActs = true;
            const params = {
                filter: { OddID: this.activeItem.OddID },
                draw: 1,
                start: 0,
                length: 500
            };
            _OrderActivityService.pagination(params, this.$fun.getUserID()).then(r => {
                this.activities = (r.data?.data || []).map(a => ({ ...a, _files: a._files || [] }));
            })
                .catch(() => this.$fun.alert("Error al cargar actividades", "error"))
                .finally(() => { this.loadingActs = false; });
        },
        storeActivity(question, payload, callback = () => { }, failCallback = () => { }) {
            this.$fun.alert(question, "question").then(r => {
                if (!r.value) {
                    failCallback();
                    return;
                }
                this.savingAct = true;
                _OrderActivityService.save(payload, this.$fun.getUserID()).then(callback).catch(() => {
                    this.savingAct = false;
                    failCallback();
                    this.$fun.alert("ERROR AL GUARDAR ACTIVIDAD", "error")
                }).finally(() => {
                    this.savingAct = false;
                });
            });
        },
        saveActivity() {
            if (!this.form.SoaDescription.trim()) {
                this.$fun.alert("EL CAMPO DESCRIPCIÓN ES OBLIGATORIO", "warning"); return;
            }

            if (this.form.SoaWeightPercent <= 0) {
                this.$fun.alert("EL CAMPO % ENCARGADO ES OBLIGATORIO", "warning"); return;
            }

            const otherW = this.activities.filter(a => !this.editingAct || a.SoaID !== this.editingAct.SoaID).reduce((s, a) => s + (parseFloat(a.SoaWeightPercent) || 0), 0);
            if (otherW + this.form.SoaWeightPercent > 100) {
                this.$fun.alert(`EL PESO TOTAL SUPERA 100%. DISPONIBLE: ${(100 - otherW).toFixed(1)}%`, "warning"); return;
            }

            const otherC = this.activities.filter(a => !this.editingAct || a.SoaID !== this.editingAct.SoaID).reduce((s, a) => s + (parseFloat(a.SoaCost) || 0), 0);
            if (this.activeItem && otherC + parseFloat(this.form.SoaCost || 0) > parseFloat(this.activeItem.OddAmount)) {
                this.$fun.alert("EL COSTO SUPERA EL MONTO DEL ÍTEM", "warning"); return;
            }

            const payload = {
                // ...(this.editingAct || {}),
                ...this.form,
                OddID: this.activeItem.OddID,
                OrdID: this.order.OrdID,
                UsrCreateID: this.$fun.getUserID(),
            };

            console.log(JSON.parse(JSON.stringify(payload)))
            this.storeActivity("¿Esta seguro de guardar la actividad?", payload, () => {
                this.cancelEdit();
                this.fetchActivities();
                this.refreshItemProgress();
                this.$fun.alert("GUARDADO CORRECTAMENTE", "success")
            });
        },
        editAct(act) {
            this.editingAct = act;
            this.fillMaxCost = false;
            this.form = {
                ...this.form,
                ...act
            };
        },
        cancelEdit() {
            this.editingAct = null;
            this.fillMaxCost = false;
            this.form = {
                SoaID: 0,
                OddID: 0,
                OrdID: 0,
                SoaDescription: "",
                SoaWeightPercent: 0,
                SoaCost: 0,
                SoaCompleted: 0,
                SoaObservation: "",
                SoaStatus: 1,
                SecStatus: 1,
                UsrCreateID: 0
            };
        },
        deleteAct(activity) {
            const payload = { ...activity, SecStatus: 0, UsrUpdateID: this.$fun.getUserID() };
            this.storeActivity("¿Eliminar esta actividad?", payload, () => {
                this.fetchActivities();
                this.refreshItemProgress();
            });
        },
        toggleComplete(activity) {
            const oldValue = activity.SoaCompleted == 1 ? 0 : 1;
            const payload = { ...activity, UsrCreateID: this.$fun.getUserID() };
            this.storeActivity("Actualizar estado de actividad?", payload, () => {
                this.fetchActivities();
                this.refreshItemProgress();
                this.$fun.alert("ACTIVIDAD ACTUALIZADA CORRECTAMENTE", "success")
            }, () => {
                activity.SoaCompleted = oldValue;
            });
        },
        refreshItemProgress() {
            if (!this.activeItem) return;
            const idx = this.orderItems.findIndex(i => i.OddID == this.activeItem.OddID);
            if (idx < 0) return;
            this.$set(this.orderItems, idx, {
                ...this.orderItems[idx],
                _progress: this.itemProgress,
                _actCount: this.activities.length,
            });
        },
        openEvidence(act) {
            this.activeAct = act;
            this.pendingFiles = [];
            this.isDragOver = false;
            this.dlgEv = true;
            this.loadEvidences(act);
        },
        loadEvidences(activity) {
            this.loadingEv = true;
            const params = {
                filter: { SoaID: activity.SoaID },
                draw: 1,
                start: 0,
                length: 500
            };
            _OrderActivityService.paginationEvidence(params, this.$fun.getUserID()).then(r => {
                const files = r.data.data || [];
                files.forEach(f => {
                    this.$set(f, '_previewUrl', null);
                    if (this.isImageFile(f.FileName)) {
                        _GenUploadFiles.getFileForPreview("RutaAnexosSAP", f.FileName).then(res => {
                            if (res && res.url) this.$set(f, '_previewUrl', res.url);
                        }).catch(() => { });
                    }
                });
                this.$set(activity, '_files', files);
            }).catch(() => { }).finally(() => { this.loadingEv = false; });
        },
        downloadEvidence(fileName) {
            this.$fun.alert("Preparando descarga...", "info", { timer: 500 });
            _GenUploadFiles.getFile("RutaAnexosSAP", fileName, false).catch(() => {
                this.$fun.alert("No se pudo descargar el archivo", "error");
            });
        },
        onDrop(e) {
            this.isDragOver = false;
            this.addPendingFiles(Array.from(e.dataTransfer.files));
        },
        onFileInput(e) {
            this.addPendingFiles(Array.from(e.target.files));
            e.target.value = "";
        },
        addPendingFiles(files) {
            const MAX = 10 * 1024 * 1024;
            files.forEach(f => {
                if (f.size > MAX) { this.$fun.alert(`"${f.name}" supera los 10 MB`, "warning"); return; }
                const isImg = f.type && f.type.startsWith('image/');
                this.pendingFiles.push({
                    file: f,
                    name: f.name,
                    size: f.size,
                    preview: isImg ? URL.createObjectURL(f) : null
                });
            });
        },
        async uploadPending() {
            if (!this.pendingFiles.length || !this.activeAct) return;
            this.uploadingFiles = true;
            let activityEvidences = [];

            for (const item of this.pendingFiles) {
                const f = item.file || item;
                const fileName = f.name;

                const uploadedFile = await this.loadFileOnServer(f, fileName, "RutaAnexosSAP", true);

                if (uploadedFile.success) {
                    activityEvidences.push({
                        EvID: 0,
                        SoaID: this.activeAct.SoaID,
                        FileName: uploadedFile.fileName,
                        PatchUrl: uploadedFile.path,
                        SecStatus: 1,
                        UsrCreateID: this.$fun.getUserID()
                    });
                } else {
                    console.log(uploadedFile.message);
                    this.$fun.alert(uploadedFile.message, "error");
                }
            }

            if (activityEvidences.length > 0) {
                this.$set(this.activeAct, 'ActivityEvidences', activityEvidences);
                console.log(JSON.parse(JSON.stringify(this.activeAct)))

                this.storeActivity("Desea guardar archivos de evidencias?", this.activeAct, () => {
                    this.pendingFiles = [];
                    this.loadEvidences(this.activeAct);
                    this.$fun.alert("ARCHIVOS SUBIDOS CORRECTAMENTE", "success");
                    this.uploadingFiles = false;
                });
            }
        },
        async loadFileOnServer(model, fileName, paramRoute = "RutaAnexosSAP", overwrite = true) {
            try {
                const response = await _GenUploadFiles.upload(model, fileName.toUpperCase(), paramRoute, overwrite);
                // console.log("loadFileOnServer", JSON.parse(JSON.stringify(response)))
                if (response.status == 200) {
                    return {
                        success: true,
                        message: response.data.message,
                        path: response.data.path,
                        fileName: response.data.fileName,
                    }
                } else {
                    return {
                        success: false,
                        message: 'Error al subir archivo en loadFileOnServer',
                    }
                }
            } catch (error) {
                console.log("Error on loadFileOnServer: ", error);
                if (Object.keys(error).length > 0) {
                    this.$fun.alert(error?.response?.data?.Message, "error");
                    return {
                        success: false,
                        message: error?.response?.data?.Message || error.message,
                    }
                } else {
                    return {
                        success: false,
                        message: error.message,
                    }
                }
            }
        },
        deleteEv(evidence, idx) {
            this.$fun.alert("¿Eliminar este archivo?", "question").then(r => {
                if (!r.value) return;
                const payload = {
                    ...evidence,
                    SecStatus: 0,
                    UsrCreateID: this.$fun.getUserID()
                };

                _OrderActivityService.saveEvidence(payload, this.$fun.getUserID()).then(() => {
                    this.$fun.alert("Archivo eliminado correctamente", "success")
                    this.loadEvidences(this.activeAct);
                }).catch(() => this.$fun.alert("Error al eliminar archivo", "error"));
            });
        },
        fileIcon(name) {
            if (!name) return "mdi-file";
            const ext = name.split(".").pop().toLowerCase();
            if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "mdi-file-image";
            if (ext === "pdf") return "mdi-file-pdf-box";
            if (["xls", "xlsx"].includes(ext)) return "mdi-file-excel";
            if (["doc", "docx"].includes(ext)) return "mdi-file-word";
            return "mdi-file-outline";
        },
        fileIconColor(name) {
            if (!name) return "grey";
            const ext = name.split(".").pop().toLowerCase();
            if (["jpg", "jpeg", "png", "gif"].includes(ext)) return "teal";
            if (ext === "pdf") return "red";
            if (["xls", "xlsx"].includes(ext)) return "green darken-1";
            if (["doc", "docx"].includes(ext)) return "blue darken-1";
            return "grey";
        },
        isImageFile(name) {
            if (!name) return false;
            const ext = name.split(".").pop().toLowerCase();
            return ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
        },
        formatSize(bytes) {
            if (bytes < 1024) return bytes + " B";
            if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
            return (bytes / 1048576).toFixed(1) + " MB";
        },
    },
};
</script>

<style scoped>
.soe-root {
    width: 100%;
    font-family: 'Inter', sans-serif;
}

.soe-hdr {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: linear-gradient(135deg, #04abc5 0%, #04abc5 100%);
    border-radius: 14px 14px 0 0;
    padding: 6px 8px;
    box-shadow: 0 4px 18px rgba(13, 71, 161, .22);
}

.soe-hdr-left {
    display: flex;
    align-items: center;
    gap: 12px;
}

.soe-hdr-icon {
    padding: 8px;
    background: rgba(255, 255, 255, .15);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.soe-hdr-title {
    color: #fff;
    font-size: 14px;
    font-weight: 700;
}

.soe-hdr-sub {
    color: rgba(255, 255, 255, .7);
    font-size: 11px;
    margin-top: 2px;
}

.soe-table {
    border-radius: 0 0 12px 12px;
}

.soe-desc-cell {
    padding: 4px 0;
}

.soe-progress-cell {
    display: flex;
    align-items: center;
    min-width: 140px;
}

.soe-empty {
    text-align: center;
    padding: 32px;
}

.soe-dlg-hdr {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: linear-gradient(135deg, #0d47a1 0%, #1976d2 100%);
    padding: 13px 16px;
}

.soe-dlg-title {
    color: #fff;
    font-size: 14px;
    font-weight: 700;
}

.soe-meta-bar {
    display: flex;
    background: #f5f7fb;
    border-bottom: 1px solid #e4e8f0;
}

.soe-meta-cell {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 10px 8px;
    border-right: 1px solid #e4e8f0;
}

.soe-meta-cell:last-child {
    border-right: none;
}

.soe-meta-lbl {
    font-size: 10px;
    color: #aaa;
    text-transform: uppercase;
    letter-spacing: .4px;
}

.soe-meta-val {
    font-size: 14px;
    font-weight: 700;
    margin-top: 2px;
}

.soe-act-form {
    background: #f8fbff;
    border-bottom: 1px solid #e4e8f0;
    padding: 12px 16px;
}

.soe-act-form-title {
    font-size: 12px;
    font-weight: 600;
    color: #1976d2;
}

.soe-field :deep(.v-input__slot) {
    min-height: 36px !important;
    font-size: 13px;
}

.soe-act-list {
    max-height: 300px;
    overflow-y: auto;
}

.soe-act-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    margin: 4px 0;
    background: #fff;
    border: 1px solid #e8edf5;
    transition: all .2s ease;
}

.soe-act-row:hover {
    border-color: #1976d2;
    box-shadow: 0 2px 10px rgba(25, 118, 210, .1);
}

.soe-act-done {
    background: #f1fff5 !important;
    border-color: #00c853 !important;
}

.soe-act-check {
    flex: 0 0 auto;
}

.soe-act-info {
    flex: 1;
    min-width: 0;
}

.soe-act-desc {
    font-size: 13px;
    font-weight: 500;
    color: #333;
}

.soe-act-obs {
    font-size: 11px;
    color: #999;
    margin-top: 1px;
}

.soe-act-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
}

.soe-act-contrib {
    flex: 0 0 90px;
    text-align: center;
}

.soe-act-ev {
    flex: 0 0 40px;
    display: flex;
    align-items: center;
}

.soe-act-actions {
    flex: 0 0 auto;
    display: flex;
    gap: 2px;
}

.soe-drop-zone {
    border: 2px dashed #cdd4e0;
    border-radius: 14px;
    text-align: center;
    margin: 16px;
    cursor: pointer;
    transition: all .25s ease;
    background: #fafbfd;
    min-height: 140px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.soe-drop-over {
    border-color: #1976d2;
    background: #e8f0fe;
    box-shadow: 0 0 0 4px rgba(25, 118, 210, .12);
}

.soe-ev-section-title {
    font-size: 11px;
    color: #666;
    font-weight: 600;
}

.soe-ev-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    border-radius: 8px;
    border: 1px solid #eee;
    margin-bottom: 4px;
    background: #fafafa;
}

.soe-ev-name {
    flex: 1;
    font-size: 12px;
    color: #444;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.soe-ev-size {
    font-size: 10px;
    color: #aaa;
    white-space: nowrap;
}

/* ══════════════════════════════════════════════════
   NUEVO DIÁLOGO DE ACTIVIDADES — Layout dos columnas
════════════════════════════════════════════════════ */
.act-card {
    font-family: 'Inter', sans-serif;
    background: #f5f7fb !important;
}

/* ── Header ── */
.act-hdr {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: linear-gradient(135deg, #0d47a1 0%, #1565c0 60%, #1976d2 100%);
    padding: 14px 20px;
    box-shadow: 0 3px 12px rgba(13, 71, 161, .3);
}

.act-hdr-icon {
    width: 36px;
    height: 36px;
    background: rgba(255, 255, 255, .18);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.act-hdr-title {
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.2;
}

.act-hdr-sub {
    color: rgba(255, 255, 255, .65);
    font-size: 11px;
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 480px;
}

.act-badge {
    font-size: 11px !important;
    font-weight: 600;
}

/* ── KPI Bar ── */
.act-kpi-bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0;
    background: #fff;
    border-bottom: 1px solid #e4e8f0;
    padding: 10px 20px;
}

.act-kpi-cell {
    display: flex;
    flex-direction: column;
    padding: 0 16px;
    flex: 1;
    min-width: 100px;
}

.act-kpi-cell:first-child {
    padding-left: 0;
}

.act-kpi-sep {
    width: 1px;
    height: 36px;
    background: #e4e8f0;
    flex-shrink: 0;
}

.act-kpi-label {
    font-size: 10px;
    color: #aaa;
    text-transform: uppercase;
    letter-spacing: .4px;
    white-space: nowrap;
}

.act-kpi-value {
    font-size: 16px;
    font-weight: 700;
    margin-top: 1px;
}

.act-kpi-warn {
    font-size: 10px;
    color: #e53935;
    font-weight: 600;
}

.act-ok {
    color: #00897b;
}

.act-over {
    color: #e53935 !important;
}

/* ── Body 2-col ── */
.act-body {
    display: flex;
    align-items: stretch;
    min-height: 0;
    max-height: calc(100vh - 120px);
}

/* LEFT panel — formulario 30% */
.act-panel-form {
    flex: 0 0 320px;
    width: 320px;
    background: #fff;
    border-right: 1px solid #e4e8f0;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 0;
    overflow-y: auto;
}

.act-form-title {
    font-size: 12px;
    font-weight: 700;
    color: #444;
    text-transform: uppercase;
    letter-spacing: .5px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
}

/* campos */
.act-field-group {
    margin-bottom: 7px;
}

.act-label {
    display: block;
    font-size: 10px;
    font-weight: 600;
    color: #555;
    margin-bottom: 2px;
    letter-spacing: .3px;
}

.act-required {
    color: #e53935;
    margin-left: 2px;
}

.act-field-hint {
    font-size: 10px;
    color: #999;
    margin-bottom: 2px;
}

.act-input :deep(.v-input__slot) {
    min-height: 30px !important;
    font-size: 12px !important;
    border-radius: 7px !important;
}

.act-input :deep(fieldset) {
    border-color: #dde3ee !important;
}

.act-input:hover :deep(fieldset) {
    border-color: #1976d2 !important;
}

.act-input :deep(.v-input__slot:focus-within fieldset) {
    border-color: #1976d2 !important;
}

/* botones form */
.act-form-actions {
    margin-top: 4px;
}

.act-btn-save {
    font-weight: 700;
    letter-spacing: .3px;
    height: 40px !important;
}

.act-btn-cancel {
    font-size: 12px !important;
}

/* RIGHT panel — lista 70% */
.act-panel-list {
    flex: 1;
    min-width: 0;
    background: #f5f7fb;
    padding: 16px 18px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.act-list-title {
    font-size: 12px;
    font-weight: 700;
    color: #444;
    text-transform: uppercase;
    letter-spacing: .5px;
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    flex-shrink: 0;
}

.act-list-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: #ccc;
}

.act-list-empty p {
    font-size: 13px;
    margin: 4px 0;
}

.act-list-scroll {
    flex: 1;
    overflow-y: auto;
    padding-right: 4px;
}

.act-list-scroll::-webkit-scrollbar {
    width: 4px;
}

.act-list-scroll::-webkit-scrollbar-thumb {
    background: #d0d7e8;
    border-radius: 4px;
}

/* tarjeta de actividad */
.act-item {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #fff;
    border: 1px solid #e8edf5;
    border-radius: 18px;
    padding: 6px 8px;
    margin-bottom: 4px;
    transition: all .2s ease;
    box-shadow: 0 1px 4px rgba(0, 0, 0, .04);
}

.act-item:hover {
    border-color: #1976d2;
    box-shadow: 0 3px 14px rgba(25, 118, 210, .12);
}

.act-item-done {
    background: #f0fdf4 !important;
    border-color: #a7f3d0 !important;
}

.act-item-editing {
    border-color: #ff9800 !important;
    box-shadow: 0 0 0 3px rgba(255, 152, 0, .12) !important;
}

.act-item-left {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 0 0 auto;
    gap: 2px;
}

.act-item-num {
    font-size: 10px;
    font-weight: 700;
    color: #c0c8d8;
    line-height: 1;
    letter-spacing: .3px;
}

.act-item-body {
    flex: 1;
    min-width: 0;
}

.act-item-desc {
    font-size: 12px;
    font-weight: 600;
    color: #2d3748;
    line-height: 1.1;
}

.act-item-obs {
    font-size: 11px;
    color: #999;
    display: flex;
    align-items: center;
}

.act-item-impact {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-right: 12px;
}

.impact-pill {
    display: flex;
    align-items: center;
    background: #f4f6f9;
    border-radius: 6px;
    padding: 4px 8px;
    font-size: 10px;
    color: #555;
    transition: all .2s;
}

.impact-pill-done {
    background: #e8f5e9;
    color: #004d40;
}

.impact-pill-pending {
    background: #fff3e0;
    color: #e65100;
}

.impact-text {
    letter-spacing: .2px;
}

.act-item-actions {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    gap: 2px;
    align-items: center;
}

/* ── Switch "Finalizar Actividad" ── */
.act-item-left {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 0 0 auto;
    gap: 4px;
}

.act-switch-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    background: #f4f6fb;
    border: 1.5px solid #e2e8f4;
    border-radius: 12px;
    padding: 8px 6px 6px;
    gap: 2px;
    min-width: 64px;
    cursor: pointer;
    transition: all .25s ease;
    user-select: none;
}

.act-switch-wrap:hover {
    border-color: #1976d2;
    background: #eef3fd;
    box-shadow: 0 2px 10px rgba(25, 118, 210, .1);
}

.act-switch-active {
    background: #f0fdf4 !important;
    border-color: #00c853 !important;
    box-shadow: 0 2px 10px rgba(0, 200, 83, .12) !important;
}

.act-switch :deep(.v-input--switch__thumb) {
    transition: all .25s ease;
}

.act-switch :deep(.v-input--selection-controls__input) {
    margin: 0 !important;
}

.act-switch-label {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
    margin-top: 2px;
}

.act-switch-label span {
    font-size: 9.5px;
    font-weight: 700;
    color: #888;
    text-transform: uppercase;
    letter-spacing: .4px;
    line-height: 1.2;
    text-align: center;
}

.act-switch-label span:first-of-type {
    color: #555;
}

.act-switch-active .act-switch-label span {
    color: #00897b;
}

.act-switch-active .act-switch-label span:first-of-type {
    color: #00c853;
    font-weight: 800;
}

.act-switch-sub {
    opacity: .7;
}

/* ── Checkbox "Monto disponible" ── */
.act-fill-check {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    user-select: none;
}

.act-fill-check-input {
    display: none;
}

.act-fill-check-box {
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform .15s ease;
}

.act-fill-check:hover .act-fill-check-box {
    transform: scale(1.15);
}

.act-fill-check-text {
    font-size: 10px;
    font-weight: 600;
    color: #888;
    letter-spacing: .3px;
}

.act-fill-check:hover .act-fill-check-text {
    color: #1976d2;
}

.act-fill-hint {
    display: flex;
    align-items: center;
    font-size: 10px;
    color: #00897b;
    font-weight: 600;
    margin-top: 4px;
    padding: 3px 8px;
    background: #e8f5e9;
    border-radius: 6px;
}

/* ── Dialog Evidencias ── */
.act-ev-card {
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2) !important;
}

.act-ev-hdr {
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 16px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.act-ev-hdr-icon {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 10px;
}

.act-ev-title {
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: .5px;
}

.act-ev-sub {
    color: #94a3b8;
    font-size: 12px;
    margin-top: 2px;
    max-width: 400px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.act-ev-close {
    background: rgba(255, 255, 255, 0.1) !important;
    transition: all .2s;
}

.act-ev-close:hover {
    background: #ef4444 !important;
    transform: rotate(90deg);
}

.soe-drop-zone {
    border: 2px dashed #cbd5e1;
    border-radius: 16px;
    background: #fff;
    text-align: center;
    padding: 40px 20px;
    cursor: pointer;
    transition: all .3s cubic-bezier(0.4, 0, 0.2, 1);
}

.soe-drop-zone:hover,
.soe-drop-over {
    border-color: #3b82f6;
    background: #eff6ff;
}

.soe-drop-zone:hover .drop-zone-icon {
    transform: translateY(-5px);
}

.drop-zone-icon {
    transition: transform .3s ease;
}

.drop-zone-title {
    color: #334155;
    font-size: 16px;
}

.drop-zone-subtitle {
    color: #64748b;
    font-size: 12px;
}

.drop-zone-btn {
    text-transform: none;
    letter-spacing: .3px;
    font-weight: 600;
}

.soe-ev-section-title {
    font-size: 13px;
    font-weight: 700;
    color: #334155;
    text-transform: uppercase;
    letter-spacing: .5px;
}

.ev-list-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.soe-ev-row {
    display: flex;
    align-items: center;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 10px 16px;
    transition: all .2s ease;
}

.soe-ev-row:hover {
    border-color: #cbd5e1;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}

.soe-ev-row-done {
    background: #f8fafc;
}

.ev-row-icon {
    background: #f1f5f9;
    border-radius: 8px;
    padding: 8px;
    margin-right: 14px;
}

.ev-row-info {
    flex: 1;
    min-width: 0;
}

.soe-ev-name {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.soe-ev-size {
    font-size: 11px;
    color: #94a3b8;
    margin-top: 2px;
}

.ev-row-actions {
    display: flex;
    gap: 4px;
}

/* ── Evidencias Horizontal Elegant Chips ── */
.ev-files-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 10px;
}

.ev-file-card {
    display: flex;
    flex-direction: row;
    align-items: center;
    background: #fff;
    border: 1px solid #e4e8f0;
    border-radius: 6px;
    padding: 6px 8px;
    transition: all 0.2s ease;
    height: 52px;
    position: relative;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
}

.ev-file-card:hover {
    border-color: #3b82f6;
    background: #f8fafc;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
}

.ev-file-card-done {
    border-color: #d1fae5;
}

.ev-file-card-done:hover {
    border-color: #10b981;
}

.ev-file-icon-wrap {
    width: 36px;
    height: 36px;
    border-radius: 4px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-right: 8px;
    overflow: hidden;
}

.ev-file-icon-wrap img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.ev-file-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: left;
}

.ev-file-name {
    font-size: 11px;
    font-weight: 600;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.2;
}

.ev-file-meta {
    font-size: 9px;
    color: #64748b;
    margin-top: 2px;
    display: flex;
    align-items: center;
    gap: 4px;
}

.ev-file-actions {
    display: flex;
    gap: 4px;
    margin-left: 4px;
    opacity: 0.5;
    transition: opacity 0.2s;
}

.ev-file-card:hover .ev-file-actions {
    opacity: 1;
}

.ev-action-btn-sm {
    width: 26px !important;
    height: 26px !important;
    background: #fff !important;
    color: #333 !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.ev-action-btn-sm:hover {
    transform: scale(1.1);
}

/* ── Media Queries Responsive ── */
@media (max-width: 900px) {
    .act-body {
        flex-direction: column;
        overflow-y: auto;
    }

    .act-panel-form {
        flex: none;
        width: 100%;
        border-right: none;
        border-bottom: 1px solid #e4e8f0;
    }

    .act-panel-list {
        flex: none;
        width: 100%;
        overflow: visible;
        /* Let the body handle scrolling */
    }

    .act-item-left {
        flex: 0 0 50px;
    }

    .act-charts-row .v-row {
        flex-direction: column;
    }

    .act-charts-row .v-col {
        max-width: 100%;
    }
}
</style>
